// Pure helpers for the Clients page (increment 5 UI). Extracted so the roster/date logic
// is unit-testable and shared between the page and its tests. `now` is injected for tests.
// Project rules live in one module shared with the client-summary API so the page and the
// cards/MCP can never disagree about which project is primary or where work belongs.
import { liveProjectsOf, primaryProjectOf, projectAttribution, projectTitle } from "../../api/src/model/projects.js";

export { liveProjectsOf, projectTitle };
export const DAY = 86400000;

// Local calendar date "YYYY-MM-DD" for `now`.
export function todayOf(now = Date.now()) {
  const d = new Date(now);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// The project a client page is about: the selected one if it is live, else the primary
// (active today → soonest upcoming → most recently ended). null when nothing is live.
export function currentProjectOf(projects, selectedId = null, now = Date.now()) {
  const live = liveProjectsOf(projects);
  return (selectedId && live.find((p) => p.id === selectedId)) || primaryProjectOf(projects, todayOf(now));
}

// Splits a client's objectives/tasks by project for the client page: the current project's
// work, each other live project's objectives (earliest start first), and "earlier" objectives
// (linked to an archived project). Unlinked work belongs to the primary project.
export function groupWorkByProject(projects, objectives, tasks, currentId, now = Date.now()) {
  const attr = projectAttribution(projects, objectives, todayOf(now));
  const objs = objectives || [];
  return {
    current: {
      objectives: objs.filter((o) => currentId && attr.ofObjective(o) === currentId),
      tasks: (tasks || []).filter((t) => currentId && attr.ofTask(t) === currentId),
    },
    others: liveProjectsOf(projects)
      .filter((p) => p.id !== currentId)
      .map((p) => ({ project: p, objectives: objs.filter((o) => attr.ofObjective(o) === p.id) }))
      .filter((g) => g.objectives.length > 0),
    earlier: objs.filter((o) => attr.ofObjective(o) == null),
  };
}

// Percentage of a date range elapsed (for the progress bar). null if dates missing.
export function progressPct(startDate, endDate, now = Date.now()) {
  if (!startDate || !endDate) return null;
  const s = new Date(startDate + "T00:00:00").getTime();
  const e = new Date(endDate + "T00:00:00").getTime();
  if (now <= s) return 0;
  if (now >= e) return 100;
  return Math.round(((now - s) / (e - s)) * 100);
}

// PM roster: projects that are upcoming (next 30d), active, or closed (last 60d), grouped by
// each project's PM — one line per project. Returns [{ pmId, items: [{ clientId, project,
// label, multi }] }]: label "" active | "upcoming" | "closed"; multi = the client has more than
// one live project (any dates), so the line is named "Client - Project".
export function buildRoster(projects, now = Date.now()) {
  const clientKey = (p) => p.clientId || (p.client ? `name:${p.client}` : `project:${p.id}`);
  const liveCount = new Map();
  for (const p of liveProjectsOf(projects)) liveCount.set(clientKey(p), (liveCount.get(clientKey(p)) || 0) + 1);
  const byPm = new Map();
  for (const p of projects || []) {
    if (p.archivedAt || !p.startDate || !p.endDate) continue;
    const start = new Date(p.startDate + "T00:00:00").getTime();
    const end = new Date(p.endDate + "T00:00:00").getTime();
    let label = null;
    if (start <= now && now <= end) label = "";
    else if (start > now && start <= now + 30 * DAY) label = "upcoming";
    else if (end < now && end >= now - 60 * DAY) label = "closed";
    else continue;
    const pmId = p.projectManager || "__unassigned__";
    if (!byPm.has(pmId)) byPm.set(pmId, []);
    byPm.get(pmId).push({ clientId: p.clientId || null, project: p, label, multi: (liveCount.get(clientKey(p)) || 0) > 1 });
  }
  const byStart = (a, b) => ((a.project.startDate || "") < (b.project.startDate || "") ? -1 : 1);
  return [...byPm.entries()].map(([pmId, items]) => ({ pmId, items: items.sort(byStart) }));
}

export function fmtDate(d) {
  if (!d) return "—";
  return new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

// Split an objective's tasks for the condensed view: open tasks by due date (undated last,
// original order kept on ties) — the first is "next up" — and completed tasks, folded away.
export function splitObjectiveTasks(tasks) {
  const all = tasks || [];
  const open = all
    .map((t, i) => ({ t, i }))
    .filter(({ t }) => t.status !== "Completed")
    .sort((a, b) => {
      const da = a.t.dueDate ? a.t.dueDate.slice(0, 10) : null;
      const db = b.t.dueDate ? b.t.dueDate.slice(0, 10) : null;
      if (da && db && da !== db) return da < db ? -1 : 1;
      if (!da !== !db) return da ? -1 : 1;
      return a.i - b.i;
    })
    .map(({ t }) => t);
  return { open, completed: all.filter((t) => t.status === "Completed"), nextUp: open[0] || null };
}

// Default assignee for a hand-created task: the PM of the given project, else of the client's
// current project. Returns a user id or null. (Template imports resolve their own roles.)
export function defaultPmFor(projects, { clientId, projectId } = {}, now = Date.now()) {
  const list = projects || [];
  const chosen = projectId ? list.find((p) => p.id === projectId) : null;
  if (chosen) return chosen.projectManager || null;
  if (!clientId) return null;
  return currentProjectOf(list.filter((p) => p.clientId === clientId), null, now)?.projectManager || null;
}
